import React from 'react';
import { graphql } from 'gatsby';
import Link from '../elements/link';
import { convertArrayToReadableText, stripUrlParkPrefix } from '../../functions/common';

interface ITeaserListItemProps {
  id: string;
  title: string;
  path: string;
  body: { summary: string };
  region: string | null;
  lat: number | null;
  lon: number | null;
  startDate: Date | null;
  endDate: Date | null;
  deliveryDate: string;
  status: string | null;
  type: string[] | [];
  showTitle: boolean;
}

const buildTeaserItem = (item: ITeaserListItemProps) => {
  const {
    id,
    title,
    path,
    body,
    region,
    lat,
    lon,
    status,
    startDate,
    endDate,
    deliveryDate,
    type,
    showTitle,
  } = item;
  const deKey = (str) => {
    return str.slice(0, 1).toUpperCase() + str.slice(1, str.length).replaceAll('_', ' ');
  };

  return title ? (
    <div className="teaser-item " key={id}>
      {showTitle === true ? (
        <h3>
          <Link to={path} className="teaser__link">
            {title}
          </Link>
        </h3>
      ) : (
        <h2>
          <Link to={path} className="teaser__link">
            {title}
          </Link>
        </h2>
      )}
      {status && (
        <p>
          <strong>Project status:</strong> {deKey(status)}
        </p>
      )}
      {deliveryDate && (
        <p>
          <strong>Delivery timeframe:</strong> {deliveryDate}
        </p>
      )}
      {type.length > 0 && (
        <p>
          <strong>Project type:</strong> {convertArrayToReadableText(type)}
        </p>
      )}
      {region && (
        <p>
          <strong>Region: </strong>
          {region}
        </p>
      )}
      {body.summary && <p>{body.summary}</p>}
    </div>
  ) : (
    <React.Fragment key={id}></React.Fragment>
  );
};

export const TeaserListParagraph = ({ node }) => {
  const { id, field_title: title, field_max_items: maxItems, field_sort_by: sortBy } = node;

  if (!id) return <React.Fragment key={id}></React.Fragment>;

  const showTitle = title && title.length > 0;

  // TODO: Add in sorting at the array level here

  const itemsList =
    // For Content Items
    node.relationships?.field_content.length > 0
      ? node.relationships?.field_content
          .filter((item) => item.relationships.field_content_item != null)
          .map((item) => {
            const node = item.relationships.field_content_item,
              data = {
                id: node.drupal_id,
                title: node.title,
                path: stripUrlParkPrefix(node.path.alias),
                body: node.body,
                region: node.relationships?.field_kakadu_region?.title || null,
                lat: node.field_kar_latitude || null,
                lon: node.field_kar_longitude || null,
                startDate: node.field_start_date || null,
                endDate: node.field_end_date || null,
                deliveryDate: node.field_timeframe || null,
                status: node.field_project_status || null,
                type: node.field_project_type || [],
                showTitle: showTitle,
              };
            return buildTeaserItem(data);
          })
      : // For View items
        node.relationships?.field_content_view?.relatedCustomDrupalView?.viewChildren.map(
          (item) => {
            const data = {
              id: item.id,
              title: item.title,
              path: stripUrlParkPrefix(item.path.alias),
              body: item.body,
              region: item.customRelatedRegion?.title || null,
              lat: item.field_kar_latitude || null,
              lon: item.field_kar_longitude || null,
              startDate: item.field_start_date || null,
              endDate: item.field_end_date || null,
              deliveryDate: item.field_timeframe || null,
              status: item.field_project_status || null,
              type: item.field_project_type || [],
              showTitle: showTitle,
            };
            return buildTeaserItem(data);
          }
        );

  const maxCountItemsList = maxItems ? itemsList.slice(0, maxItems) : itemsList;

  return maxCountItemsList ? (
    <div
      className="paragraph_accordion_container section--highlight-background py-large"
      key={title}
    >
      <div className="grid-wrapper">
        <div className="grid-row clearfix">
          <div className="grid-col grid-col--8 grid-col--push-2 tb-grid-col--10 tb-grid-col--push-1 ph-grid-col--12 ph-grid-col--push-0">
            <div className="copy teaser-box">
              {title && <h2>{title}</h2>}
              <div>{maxCountItemsList}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : (
    <React.Fragment key={id}></React.Fragment>
  );
};

export const fragment = graphql`
  fragment ParagraphTeaserList on paragraph__teaser_list {
    id
    drupal_id
    internal {
      type
    }
    field_title
    field_max_items
    field_sort_by
    relationships {
      field_content {
        drupal_id
        field_override_read_more_text
        field_override_summary_text
        relationships {
          field_content_item {
            ...NodeDataEvent
            ...NodeDataNews
            ...NodeDataPage
            ...NodeDataPlace
            ...NodeDataProject
            ...NodeDataPublication
            ...NodeDataRegion
            ...NodeDataTour
            ...NodeDataWildlife
          }
        }
      }
      field_content_view {
        id
        drupal_id
        field_view
        relatedCustomDrupalView {
          ...CustomDataDrupalView
        }
      }
    }
  }
`;
